/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import clsx from "clsx";
import { CloseIcon } from "components/Icons";
import { useGetMaxAmountToWrap } from "hooks/wrap/useGetMaxAmountToWrap";
import {
  FocusEventHandler,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { default as CurrencyInputOriginal } from "react-currency-input-field";
import { withIsVisible } from "utils/reactUtils";
import { useBalanceForSymbol } from "../../../hooks/useBalanceForSymbol";
import { useCanQuote } from "../../../hooks/useCanQuote";
import { useGetTokenDecimals } from "../../../hooks/useGetTokenDecimals";
import { useIsInputOverflow } from "../../../hooks/useIsInputOverflow";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { Token } from "../../../store/global/TokenEnum";
import { Amount } from "../../../types/amount";
import {
  getDecimalSeparator,
  isZero,
  parseAmountForInput,
  parseInputToAmount,
} from "../../../utils/amountUtils";
import { isNativeToken } from "../../../utils/wrapUtils";
import { Text } from "../Text";

interface CurrencyInputProps {
  value: Amount | undefined;
  onValueChange?: (value: Amount, wasNumberPressed: boolean) => void;
  symbol?: Token;
  maxButton?: { symbol: Token };
  setIsMax?: React.Dispatch<SetStateAction<boolean>>;
  clearButton?: boolean;
  classNames?: Record<string, boolean>;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  testId?: string;
  prefix?: string;
  style?: SerializedStyles;
}

export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onValueChange,
  symbol,
  classNames,
  placeholder,
  disabled,
  id,
  testId,
  prefix,
  style,
  maxButton,
  setIsMax,
  clearButton = false,
}) => {
  const theme = GlobalStore.useState(s => s.theme);
  const network = GlobalStore.useState(s => s.network);
  const decimalSeparator = useMemo(getDecimalSeparator, [getDecimalSeparator]);
  const [draftValue, setDraftValue] = useState<string>(parseAmountForInput(value));
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isOverflow = useIsInputOverflow(inputRef);
  const canQuote = useCanQuote();
  const maxBalance = useBalanceForSymbol(maxButton?.symbol);
  const getTokenDecimals = useGetTokenDecimals();
  const getMaxAmountToWrap = useGetMaxAmountToWrap();

  const handleChange = async (inputtedValue: string | undefined, wasNumberPressed: boolean) => {
    // Prevent user from typing if decimal places of token is exceeded
    const decimalPlaces = inputtedValue?.includes(decimalSeparator)
      ? inputtedValue.split(decimalSeparator)[1].length
      : undefined;
    if (symbol && decimalPlaces && decimalPlaces > getTokenDecimals(symbol)) return;

    try {
      if (inputtedValue === undefined) {
        setDraftValue("");
      } else {
        setDraftValue(inputtedValue);
      }
      const newAmount = parseInputToAmount(inputtedValue);
      if (symbol != null && setIsMax != null) {
        const maxAmount = await getMaxAmountToWrap(symbol);
        if (maxAmount?.value != null && newAmount?.value.eq(maxAmount.value)) {
          setIsMax(true);
        } else {
          setIsMax(false);
        }
      }
      if (newAmount == null) return;

      if (onValueChange != null) {
        onValueChange(newAmount, wasNumberPressed);
      }
    } catch (error) {
      console.error(`Cannot parse input amount: ${String(value)}`);
      Sentry.captureException(`Cannot parse input amount`, {
        extra: { e: error },
      });
    }
  };

  const handleFocus: FocusEventHandler<HTMLInputElement> = useCallback(
    event => {
      if (isZero(draftValue)) {
        setDraftValue("");
      }
      event.target.select();
    },
    [draftValue]
  );

  const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(() => {
    const parsedValue = parseAmountForInput(parseInputToAmount(draftValue));
    setDraftValue(parsedValue);
  }, [draftValue]);

  const handleMaxBalance = async () => {
    if (disabled) return;
    if (maxBalance && maxButton) {
      if (setIsMax != null) {
        setIsMax(true);
      }
      if (maxButton && isNativeToken(maxButton.symbol, network)) {
        const amountToWrap = await getMaxAmountToWrap(maxButton.symbol);
        if (amountToWrap !== null) {
          // Use wrap utils
          await handleChange(parseAmountForInput(amountToWrap), true);
        }
      } else {
        await handleChange(parseAmountForInput(maxBalance.balance), true);
      }
    }
    if (canQuote) {
      GlobalStore.update(s => {
        s.isRequestingQuote = true;
      });
    }
  };

  /* This is to account for when the user clicks balance
   *  Update draftValue if onValueChange & value changes as otherwise draftValue isn't aware we clicked the balance
   * */

  useEffect(() => {
    if (value === undefined || value?.value.eq(0)) {
      setDraftValue("");
    } else if (value) {
      setDraftValue(value.value.toString());
    }
  }, [onValueChange, value]);

  const MaxButton = withIsVisible(() => (
    <MaxButtonContainer
      className={`input-button d-sm-block ${disabled ? "disabled" : ""}`}
      onClick={() => void handleMaxBalance()}
    >
      <Text textStyle="Title" size="XS" as="span">
        Max
      </Text>
    </MaxButtonContainer>
  ));

  const ClearButton = withIsVisible(() => (
    <ClearButtonContainer
      onMouseDown={() => {
        handleChange("", false).catch(e => console.warn(e));
      }}
    >
      <CloseIcon width={22} height={22} color={theme.font.primary.default} />
    </ClearButtonContainer>
  ));

  const inputStyle = css`
    :focus {
      outline: 1px solid ${theme.button.primary.surface.active};
    }

    ${style}
  `;

  return (
    <Container>
      <CurrencyInputOriginal
        intlConfig={{ locale: navigator.language }}
        decimalsLimit={36}
        allowNegativeValue={false}
        disableAbbreviations={true}
        disableGroupSeparators={true}
        decimalSeparator={decimalSeparator}
        value={draftValue}
        data-sentry-mask="true"
        inputMode="decimal"
        autoComplete="off"
        autoCorrect="off"
        placeholder={placeholder ?? `0${decimalSeparator}0`}
        minLength={1}
        maxLength={79}
        spellCheck="false"
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled ?? false}
        className={clsx({
          "is-overflowed": isOverflow,
          ...classNames,
        })}
        onValueChange={inputtedValue => {
          const wasNumberPressed = inputtedValue !== undefined;
          if (inputtedValue?.at(-1) === ".") {
            // Updates local state only
            setDraftValue(inputtedValue);
          } else {
            // Updates both local and GlobalStore
            handleChange(inputtedValue, wasNumberPressed).catch(e => console.warn(e));
          }
        }}
        transformRawValue={rawValue => {
          return rawValue === decimalSeparator ? `0${decimalSeparator}` : rawValue;
        }}
        prefix={prefix}
        ref={inputRef}
        id={id}
        data-testid={testId}
        name="amountInput"
        css={inputStyle}
      />{" "}
      <MaxButton isVisible={maxButton != null} />
      <ClearButton isVisible={clearButton && draftValue !== ""} />
    </Container>
  );
};

// Sub-components

const Container = styled.div`
  position: relative;
`;

const MaxButtonContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 7px;
`;

const ClearButtonContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: pointer;
`;
